/**
 * Shared timezone list used across all scheduling components.
 *
 * IMPORTANT: `value` fields are IANA timezone IDs (e.g. "Asia/Kolkata").
 * Java's ZoneId.of() and Quartz Scheduler only accept IANA IDs —
 * Windows-style names like "IST" or "Mountain Standard Time" are NOT valid.
 *
 * Labels are kept human-readable for the UI dropdown display.
 */
export interface TimezoneOption {
    value: string;   // IANA timezone ID — safe for Java ZoneId.of() and Quartz
    label: string;   // Human-readable display label for the UI
}

export const TIMEZONE_ZONES: TimezoneOption[] = [
    { value: 'Asia/Kolkata',             label: 'IST - (GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
    { value: 'Etc/GMT+12',               label: 'BIT - (GMT-12:00) International Date Line West' },
    { value: 'Pacific/Midway',           label: 'SMST - (GMT-11:00) Midway Island, Samoa' },
    { value: 'Pacific/Honolulu',         label: 'HAST - (GMT-10:00) Hawaii' },
    { value: 'America/Anchorage',        label: 'AK - (GMT-09:00) Alaska' },
    { value: 'America/Los_Angeles',      label: 'PT - (GMT-08:00) Pacific Time (US and Canada), Tijuana' },
    { value: 'America/Phoenix',          label: 'MST - (GMT-07:00) Arizona' },
    { value: 'America/Chihuahua',        label: 'MSTM - (GMT-07:00) Chihuahua, La Paz, Mazatlan' },
    { value: 'America/Denver',           label: 'MT - (GMT-07:00) Mountain Time (US and Canada)' },
    { value: 'America/Mexico_City',      label: 'CST - (GMT-06:00) Guadalajara, Mexico City, Monterrey' },
    { value: 'America/Chicago',          label: 'CT - (GMT-06:00) Central Time (US and Canada)' },
    { value: 'America/Guatemala',        label: 'CAST - (GMT-06:00) Central America' },
    { value: 'America/Regina',           label: 'CCST - (GMT-06:00) Saskatchewan' },
    { value: 'America/Indiana/Indianapolis', label: 'EST - (GMT-05:00) Indiana (East)' },
    { value: 'America/Bogota',           label: 'SAPST - (GMT-05:00) Bogota, Lima, Quito' },
    { value: 'America/New_York',         label: 'ET - (GMT-05:00) Eastern Time (US and Canada)' },
    { value: 'America/Manaus',           label: 'CBST - (GMT-04:00) Manaus' },
    { value: 'America/Caracas',          label: 'SAWST - (GMT-04:00) Caracas, La Paz' },
    { value: 'America/Santiago',         label: 'PSAST - (GMT-04:00) Santiago' },
    { value: 'America/Halifax',          label: 'AST - (GMT-04:00) Atlantic Time (Canada)' },
    { value: 'America/St_Johns',         label: 'NST - (GMT-03:30) Newfoundland and Labrador' },
    { value: 'America/Argentina/Buenos_Aires', label: 'SAEST - (GMT-03:00) Buenos Aires, Georgetown' },
    { value: 'America/Godthab',          label: 'GNST - (GMT-03:00) Greenland' },
    { value: 'America/Sao_Paulo',        label: 'ESAST - (GMT-03:00) Brasilia' },
    { value: 'Atlantic/South_Georgia',   label: 'UTC02 - (GMT-02:00) Mid-Atlantic' },
    { value: 'Atlantic/Cape_Verde',      label: 'CVT - (GMT-01:00) Cape Verde Islands' },
    { value: 'Atlantic/Azores',          label: 'AZOST - (GMT-01:00) Azores' },
    { value: 'Africa/Casablanca',        label: 'GST - (GMT+00:00) Casablanca, Monrovia' },
    { value: 'Europe/London',            label: 'GMT - (GMT+00:00) Greenwich Mean Time: Dublin, Edinburgh, Lisbon, London' },
    { value: 'Europe/Berlin',            label: 'WET - (GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna' },
    { value: 'Africa/Lagos',             label: 'ECT - (GMT+01:00) West Central Africa' },
    { value: 'Europe/Paris',             label: 'RST - (GMT+01:00) Brussels, Copenhagen, Madrid, Paris' },
    { value: 'Europe/Sarajevo',          label: 'CEST - (GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb' },
    { value: 'Europe/Belgrade',          label: 'CET - (GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague' },
    { value: 'Africa/Windhoek',          label: 'NMST - (GMT+02:00) Windhoek' },
    { value: 'Africa/Harare',            label: 'SAST - (GMT+02:00) Harare, Pretoria' },
    { value: 'Asia/Jerusalem',           label: 'ISST - (GMT+02:00) Jerusalem' },
    { value: 'Europe/Athens',            label: 'GTBST - (GMT+02:00) Athens, Bucharest, Istanbul' },
    { value: 'Europe/Helsinki',          label: 'EET - (GMT+02:00) Helsinki, Kiev, Riga, Sofia, Tallinn, Vilnius' },
    { value: 'Africa/Cairo',             label: 'EGST - (GMT+02:00) Cairo' },
    { value: 'Europe/Minsk',             label: 'EEST - (GMT+02:00) Minsk' },
    { value: 'Europe/Moscow',            label: 'MSK - (GMT+03:00) Moscow, St. Petersburg, Volgograd' },
    { value: 'Africa/Nairobi',           label: 'EAT - (GMT+03:00) Nairobi' },
    { value: 'Asia/Baghdad',             label: 'ARST - (GMT+03:00) Baghdad' },
    { value: 'Asia/Riyadh',              label: 'ABST - (GMT+03:00) Kuwait, Riyadh' },
    { value: 'Asia/Tehran',              label: 'IRST - (GMT+03:30) Tehran' },
    { value: 'Asia/Tbilisi',             label: 'GET - (GMT+04:00) Tbilisi' },
    { value: 'Asia/Baku',                label: 'AZT - (GMT+04:00) Baku' },
    { value: 'Asia/Yerevan',             label: 'AMT - (GMT+04:00) Yerevan' },
    { value: 'Asia/Dubai',               label: 'ARBST - (GMT+04:00) Abu Dhabi, Muscat' },
    { value: 'Asia/Kabul',               label: 'AFT - (GMT+04:30) Kabul' },
    { value: 'Asia/Karachi',             label: 'WAST - (GMT+05:00) Islamabad, Karachi, Tashkent' },
    { value: 'Asia/Calcutta',            label: 'IST - (GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
    { value: 'Asia/Katmandu',            label: 'NST - (GMT+05:45) Kathmandu' },
    { value: 'Asia/Almaty',              label: 'CAST - (GMT+06:00) Astana, Dhaka' },
    { value: 'Asia/Rangoon',             label: 'MYST - (GMT+06:30) Yangon (Rangoon)' },
    { value: 'Asia/Bangkok',             label: 'ICT - (GMT+07:00) Bangkok, Hanoi, Jakarta' },
    { value: 'Asia/Shanghai',            label: 'CST - (GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi' },
    { value: 'Asia/Taipei',              label: 'TST - (GMT+08:00) Taipei' },
    { value: 'Asia/Singapore',           label: 'SGT - (GMT+08:00) Kuala Lumpur, Singapore' },
    { value: 'Australia/Perth',          label: 'WST - (GMT+08:00) Perth' },
    { value: 'Asia/Tokyo',               label: 'JST - (GMT+09:00) Osaka, Sapporo, Tokyo' },
    { value: 'Asia/Seoul',               label: 'KST - (GMT+09:00) Seoul' },
    { value: 'Australia/Adelaide',       label: 'ACST - (GMT+09:30) Adelaide' },
    { value: 'Australia/Darwin',         label: 'ACST - (GMT+09:30) Darwin' },
    { value: 'Australia/Brisbane',       label: 'AEST - (GMT+10:00) Brisbane' },
    { value: 'Australia/Sydney',         label: 'AEST - (GMT+10:00) Canberra, Melbourne, Sydney' },
    { value: 'Asia/Vladivostok',         label: 'VLAT - (GMT+10:00) Vladivostok' },
    { value: 'Pacific/Guam',             label: 'ChST - (GMT+10:00) Guam, Port Moresby' },
    { value: 'Asia/Magadan',             label: 'MAGT - (GMT+11:00) Magadan, Solomon Islands, New Caledonia' },
    { value: 'Pacific/Fiji',             label: 'FJT - (GMT+12:00) Fiji, Kamchatka, Marshall Islands' },
    { value: 'Pacific/Auckland',         label: 'NZST - (GMT+12:00) Auckland, Wellington' },
    { value: 'Pacific/Tongatapu',        label: 'TOT - (GMT+13:00) Nuku\'alofa' },
];
